import { useEffect, useRef, useState } from 'react';
import { Wand2, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import VariablePicker from '../VariablePicker';

// Accepts either an array of blocks or a full Block Kit object { blocks: [...] }.
// Variables {{...}} are allowed inside string values and resolved by the backend.
function validate(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return { ok: true, count: 0 };
  try {
    const parsed = JSON.parse(trimmed);
    const blocks = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.blocks)
        ? parsed.blocks
        : null;
    if (!blocks) return { ok: false, error: 'Expected an array of blocks or { "blocks": [...] }.' };
    if (blocks.length === 0) return { ok: true, count: 0 };
    const bad = blocks.findIndex((b) => !b || typeof b !== 'object' || typeof b.type !== 'string');
    if (bad >= 0) return { ok: false, error: `Block #${bad + 1} is missing a string "type" field.` };
    return { ok: true, count: blocks.length };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

export default function SlackRawJsonField({ value, onChange }) {
  const [status, setStatus] = useState({ ok: true, count: 0 });
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setStatus(validate(value));
  }, [value]);

  const handleChange = (next) => {
    onChange(next);
  };

  const handleFormat = () => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    try {
      const parsed = JSON.parse(trimmed);
      onChange(JSON.stringify(parsed, null, 2));
    } catch {
      // leave as-is; status will show the error
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const parsed = JSON.parse(text);
      onChange(JSON.stringify(parsed, null, 2));
    } catch {
      onChange(text);
    }
    e.target.value = '';
  };

  const handleInsertVariable = (variable) => {
    const el = textareaRef.current;
    if (!el) {
      onChange((value || '') + variable);
      return;
    }
    const start = el.selectionStart ?? (value || '').length;
    const end = el.selectionEnd ?? start;
    const current = value || '';
    const updated = current.slice(0, start) + variable + current.slice(end);
    onChange(updated);
    requestAnimationFrame(() => {
      const pos = start + variable.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-gray-200 rounded hover:bg-gray-50"
            title="Upload .json file"
          >
            <Upload size={11} />
            Upload JSON
          </button>
          <button
            type="button"
            onClick={handleFormat}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-gray-200 rounded hover:bg-gray-50"
            title="Pretty-print JSON"
          >
            <Wand2 size={11} />
            Format
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleFile}
          />
        </div>
        <VariablePicker onInsert={handleInsertVariable} />
      </div>

      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => handleChange(e.target.value)}
        rows={14}
        spellCheck={false}
        placeholder={`[
  {
    "type": "section",
    "text": { "type": "mrkdwn", "text": "Hello {{user.name}}" }
  }
]`}
        className="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-y"
      />

      <div className="flex items-start gap-1.5 text-[11px]">
        {status.ok ? (
          <>
            <CheckCircle2 size={12} className="text-green-600 mt-0.5 shrink-0" />
            <span className="text-gray-500">
              {status.count > 0
                ? `Valid JSON · ${status.count} block${status.count === 1 ? '' : 's'}`
                : 'Empty — paste your Block Kit JSON.'}
            </span>
          </>
        ) : (
          <>
            <AlertCircle size={12} className="text-red-500 mt-0.5 shrink-0" />
            <span className="text-red-600">{status.error}</span>
          </>
        )}
      </div>

      <p className="text-[11px] text-gray-400">
        Paste output from{' '}
        <a
          href="https://app.slack.com/block-kit-builder"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          Slack&apos;s Block Kit Builder
        </a>
        . Variables like <code className="font-mono">{'{{user.email}}'}</code> are resolved at runtime.
      </p>
    </div>
  );
}
